import React from "react";
import { FileText, ShieldCheck, Mail, ClipboardList, Image, Download, AlertTriangle } from "lucide-react";

const DOCS = [
  {
    icon: FileText,
    accent: "#7737F6",
    title: "UK Patent Specification",
    filename: "EKSELENS360-UK-Patent-Application-v4.docx",
    size: "v4 · ~27 KB",
    description:
      "Full draft specification (v4) for the UK IPO online filing pathway — title, field, background (neutralised consulting language), [0010A] technical-problem framing, [0023A] precision-and-rounding contract, [0026A]–[0026G] five-layer write-guard, [0038A] rate-card payload/latency framing, claims 1–17 (two independent method claims, hash-gated tamper-evidence), de-duplicated abstract.",
    testid: "legal-download-specification",
  },
  {
    icon: Image,
    accent: "#7737F6",
    title: "Informal Patent Drawings (Figures 1–8)",
    filename: "EKSELENS360-Formal-Drawings-v1.pdf",
    size: "v1 · PDF · ~98 KB",
    description:
      "Eight-page monochrome line-art drawings file generated programmatically per UK IPO conventions: FIG. 1 system block diagram, FIG. 2 method flow, FIG. 3 structural write-path isolation + 6-layer defence list, FIG. 4 JSON schema with no score field, FIG. 5 evidence-reconciliation flow, FIG. 6 rate-card pricing flow, FIG. 7 radar + portfolio polygons, FIG. 8 audited cost-line output. INFORMAL — your CIPA attorney may have a patent illustrator redraw before grant; these are filing-quality at priority date under r.14 Patents Rules 2007.",
    testid: "legal-download-drawings",
  },
  {
    icon: ClipboardList,
    accent: "#7737F6",
    title: "Cheat-sheet — UK IPO online filing pathway",
    filename: "EKSELENS360-Combined-Application-Form-Cheatsheet-v4.docx",
    size: "v4 · ~15 KB",
    description:
      "Field-by-field crib for the UK IPO online filing pathway (post April 2026). £75 application + £200 search = £275 total at filing. 17 main claims, two independent method claims, well below the 25-claim excess-fee threshold. PCT restoration nuance noted. Address-for-service swap note included.",
    testid: "legal-download-cheatsheet",
  },
  {
    icon: Mail,
    accent: "#7737F6",
    title: "Attorney Instruction Letter",
    filename: "EKSELENS360-Attorney-Instruction-Letter-v4.docx",
    size: "v4 · ~19 KB",
    description:
      "Copy-paste email body for a CIPA-qualified patent attorney (v4). Email & phone filled. Ownership-first ordering. Claim 5 flagged as divisional candidate. Five test-questions for the first 30-min call. Firm priority: EIP → Mathys & Squire → HGF.",
    testid: "legal-download-instruction-letter",
  },
  {
    icon: ShieldCheck,
    accent: "#7737F6",
    title: "Pre-filing Disclosure Audit Checklist",
    filename: "EKSELENS360-Disclosure-Audit-Checklist-v4.docx",
    size: "v4 · ~17 KB",
    description:
      "40+ surfaces to audit before filing day (ekselens.com, Lite flow, ERIO endpoints, view-source, marketing, GitHub, AI-tool deployment artefacts, pre-filing draft-spec disclosure, prior-art landscape) with attorney sign-off block.",
    testid: "legal-download-disclosure-audit",
  },
];

const DocCard = ({ doc }) => {
  const Icon = doc.icon;
  const href = `/downloads/${doc.filename}`;
  return (
    <a
      href={href}
      download={doc.filename}
      data-testid={doc.testid}
      className="group block border border-zinc-200 bg-white px-6 py-6 hover:border-[#7737F6] transition-colors"
    >
      <div className="flex items-start gap-4">
        <span
          className="flex h-10 w-10 items-center justify-center border border-zinc-200 group-hover:border-[#7737F6] transition-colors"
          style={{ color: doc.accent }}
        >
          <Icon size={18} strokeWidth={1.6} />
        </span>
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-[15px] font-medium text-black">{doc.title}</h3>
            <span className="text-[11px] tracking-[0.16em] uppercase text-zinc-400">.docx · {doc.size}</span>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-zinc-600">{doc.description}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-[12px] font-medium tracking-[0.16em] uppercase text-[#7737F6] group-hover:gap-3 transition-all">
            <Download size={13} strokeWidth={1.8} />
            Download
          </div>
        </div>
      </div>
    </a>
  );
};

const FILING = {
  applicationNumber: "GB2613762.0",
  applicantReference: "EKSELENS360",
  filingDate: "15 June 2026",
  priorityDate: "15 June 2026",
  pctDeadline: "15 June 2027",
  pctDiaryAlert: "15 April 2027",
  expectedSearchReport: "~15 December 2026",
  expectedPublication: "~15 December 2027",
};

const LegalIpPanel = () => {
  return (
    <div className="space-y-8" data-testid="legal-ip-panel">
      <header className="border-b border-zinc-200 pb-6">
        <p className="text-[11px] font-medium tracking-[0.24em] uppercase text-[#7737F6]">Legal &amp; IP · Internal</p>
        <h2 className="mt-2 text-2xl text-black">EKSELENS360 — UK Patent Filing Record</h2>
        <p className="mt-3 text-[13px] leading-relaxed text-zinc-600 max-w-3xl">
          UK national patent application filed at the UK Intellectual Property Office on the priority date below.
          Filed with search at filing date; substantive examination deferred. PCT international filing decision to be
          taken at month 10.
        </p>
      </header>

      <div className="border border-emerald-200 bg-emerald-50 px-5 py-5" data-testid="legal-filing-record">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} strokeWidth={1.8} className="mt-0.5 flex-none text-emerald-700" />
          <div className="flex-1">
            <p className="text-[11px] font-medium tracking-[0.24em] uppercase text-emerald-800">Filed · Priority secured</p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-[13px] text-emerald-950">
              <div><span className="text-emerald-700/70">Application number:</span> <strong className="font-semibold" data-testid="filing-application-number">{FILING.applicationNumber}</strong></div>
              <div><span className="text-emerald-700/70">Applicant reference:</span> <strong className="font-semibold">{FILING.applicantReference}</strong></div>
              <div><span className="text-emerald-700/70">Filing &amp; priority date:</span> <strong className="font-semibold">{FILING.filingDate}</strong></div>
              <div><span className="text-emerald-700/70">PCT priority deadline:</span> <strong className="font-semibold">{FILING.pctDeadline}</strong></div>
              <div><span className="text-emerald-700/70">PCT diary alert (month 10):</span> <strong className="font-semibold">{FILING.pctDiaryAlert}</strong></div>
              <div><span className="text-emerald-700/70">Expected search report:</span> <strong className="font-semibold">{FILING.expectedSearchReport}</strong></div>
              <div><span className="text-emerald-700/70">Expected publication (~18m):</span> <strong className="font-semibold">{FILING.expectedPublication}</strong></div>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-zinc-200 bg-zinc-50 px-5 py-4">
        <div className="flex gap-3">
          <AlertTriangle size={16} strokeWidth={1.8} className="mt-0.5 flex-none text-zinc-700" />
          <div className="text-[12px] leading-relaxed text-zinc-800">
            <strong className="font-semibold">Post-filing rule:</strong> the priority date is now fixed. Marketing
            disclosure of the four technical features as filed is no longer a novelty risk for this UK application.
            However, before PCT filing in {FILING.pctDeadline.slice(-4)}, do not publish technical content
            <em> beyond</em> what is in the application — new disclosure could limit what can be added at PCT. Confirm
            any new marketing copy with your attorney.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {DOCS.map((d) => (
          <DocCard key={d.filename} doc={d} />
        ))}
      </div>

      <footer className="border-t border-zinc-200 pt-6 text-[12px] text-zinc-500 space-y-2">
        <p>
          <span className="font-medium text-zinc-700">Applicant &amp; inventor:</span> Navinnarine Sahadeo (sole) ·
          152 Lillington Road, Leamington Spa, Warwickshire, CV32 6LN, United Kingdom.
        </p>
        <p>
          <span className="font-medium text-zinc-700">Markdown sources:</span> the originals live in{" "}
          <code className="px-1.5 py-0.5 bg-zinc-100 text-zinc-700 text-[11px]">/app/memory/</code> in the project
          repository. Word versions are regenerated from those sources via Pandoc and served from{" "}
          <code className="px-1.5 py-0.5 bg-zinc-100 text-zinc-700 text-[11px]">/app/frontend/public/downloads/</code>.
        </p>
      </footer>
    </div>
  );
};

export default LegalIpPanel;
